/**
 * Field mapping functions for converting legacy HTML fields to React Flow node data
 * Each function maps legacy HTML fields to React Flow node data for a specific node type
 */
import { NodeType } from '../../shared/reactFlowTypes'
import type {
  AutoAttendantNodeData,
  AutoAttendantOption,
  CallDistributionNodeData,
  CallDistributionOption,
  ChangeCallerIdNodeData,
  ConferenceRoomNodeData,
  DayNightNodeData,
  DialByNameNodeData,
  ExternalTransferNodeData,
  FaxDetectNodeData,
  InteractiveWebhookNodeData,
  InteractiveWebhookOption,
  JumpToSwitchboardNodeData,
  LeaveVoicemailNodeData,
  QueueNodeData,
  ScheduleNodeData,
  SoundClipNodeData,
  WaitNodeData,
} from '../../shared/reactFlowTypes'
import type { HtmlFieldRecord, NodeDataRecord } from '../../shared/typeUtils'
import type { LegacyFormatNode } from '../index'
import { stringToBoolean } from '../utils'
import {
  extractTarget,
  getFieldValueFromHtml,
  getStringFieldValue,
  isNodeType,
  mapField,
  mapFieldWithNullConversion,
  mapOnInvalidAndOnTimeout,
  mapTimeout,
  normalizeChildrenArray,
} from './utils'

/**
 * Map schedule node fields from legacy HTML to React Flow node data
 * Legacy format stores schedule_id, business_hours, and timezone in HTML.
 * Connection targets (whenOpen, whenClosed, holiday) can be in HTML fields
 * or in endpoints.sources[].target_uuid (extracted from edges).
 */
export function mapScheduleFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'schedule', NodeType.Schedule)) {
    const scheduleNodeData = nodeData as ScheduleNodeData & NodeDataRecord

    // Map schedule_id
    mapField(html, scheduleNodeData, 'schedule_id', 'scheduleId')
    mapField(html, scheduleNodeData, 'schedule_uuid', 'scheduleUuid')
    // Map business_hours to businessHours
    mapField(html, scheduleNodeData, 'business_hours', 'businessHours')
    // Map timezone (already camelCase, but ensure it's mapped)
    mapField(html, scheduleNodeData, 'timezone', 'timezone')
    // Map connection fields from HTML (if present)
    mapField(html, scheduleNodeData, 'when_open', 'whenOpen')
    mapField(html, scheduleNodeData, 'when_closed', 'whenClosed')
    mapField(html, scheduleNodeData, 'holiday', 'holiday')
  } else if ('schedule_id' in html) {
    nodeData.scheduleId = String(html.schedule_id)
  }
}

/**
 * Map leave voicemail node fields from legacy HTML to React Flow node data
 */
export function mapLeaveVoicemailFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'leave_voicemail', NodeType.LeaveVoicemail)) {
    const leaveVoicemailNodeData = nodeData as LeaveVoicemailNodeData &
      NodeDataRecord

    const extensionId = getStringFieldValue(html, [
      'extension_id',
      'extensionId',
      'extension',
    ])
    if (extensionId) {
      leaveVoicemailNodeData.extensionId = extensionId
    }

    // Map message_type + sound_clip_id to soundClipId
    const messageType = getStringFieldValue(html, ['message_type'])
    // Get raw sound_clip_id to distinguish between string "null" and JSON null
    const soundClipIdRaw = getFieldValueFromHtml(html, ['sound_clip_id'])
    const soundClipId =
      soundClipIdRaw !== undefined && soundClipIdRaw !== null
        ? String(soundClipIdRaw)
        : undefined

    if (messageType === '-1' && soundClipId && soundClipId !== 'null') {
      // Sound clip selected - use the sound clip ID
      leaveVoicemailNodeData.soundClipId = soundClipId
    } else if (messageType === '0' || !messageType) {
      // Defined by extension (default)
      // Only set "defined_by_extension" if sound_clip_id is the string "null"
      // If sound_clip_id is JSON null (undefined), leave soundClipId undefined
      // getFieldValueFromHtml returns undefined when value is JSON null, and "null" when it's string "null"
      if (soundClipIdRaw === 'null') {
        // sound_clip_id is the string "null" - map to "defined_by_extension"
        leaveVoicemailNodeData.soundClipId = 'defined_by_extension'
      }
      // If soundClipIdRaw is undefined (JSON null), don't set soundClipId (leave it undefined)
    } else if (messageType === '-2') {
      // System generated greeting
      leaveVoicemailNodeData.soundClipId = 'system_generated'
    } else if (messageType === '-3') {
      // No greeting
      leaveVoicemailNodeData.soundClipId = 'no_greeting'
    } else {
      // Fallback: use sound_clip_id if available, otherwise default
      if (soundClipId && soundClipId !== 'null') {
        leaveVoicemailNodeData.soundClipId = soundClipId
      } else {
        leaveVoicemailNodeData.soundClipId = 'defined_by_extension'
      }
    }

    mapFieldWithNullConversion(
      html,
      leaveVoicemailNodeData,
      'sound_clip_uuid',
      'soundClipUuid',
    )
  }
}

/**
 * Map external transfer node fields from legacy HTML to React Flow node data
 */
export function mapExternalTransferFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (
    kind === 'external_number' ||
    kind === 'external_transfer' ||
    nodeType === NodeType.ExternalTransfer
  ) {
    const externalTransferNodeData = nodeData as ExternalTransferNodeData &
      NodeDataRecord

    const phoneNumber = getStringFieldValue(html, [
      'phone_number',
      'phoneNumber',
      'number',
    ])
    if (phoneNumber) {
      externalTransferNodeData.phoneNumber = phoneNumber
    }

    mapTimeout(html, externalTransferNodeData)

    const sipServer = getStringFieldValue(html, ['sip_server', 'sipServer'])
    if (sipServer) {
      externalTransferNodeData.sipServer = sipServer
    }

    if (
      'pass_through_caller_id' in html &&
      html.pass_through_caller_id !== undefined
    ) {
      externalTransferNodeData.passThroughCallerId = stringToBoolean(
        html.pass_through_caller_id,
      )
    } else if (
      'passThroughCallerId' in html &&
      html.passThroughCallerId !== undefined
    ) {
      externalTransferNodeData.passThroughCallerId = stringToBoolean(
        html.passThroughCallerId,
      )
    } else {
      externalTransferNodeData.passThroughCallerId = true
    }

    mapFieldWithNullConversion(
      html,
      externalTransferNodeData,
      'phone_number_id',
      'phoneNumberId',
    )

    mapFieldWithNullConversion(
      html,
      externalTransferNodeData,
      'transfer_company',
      'transferCompany',
    )

    // CRITICAL: Legacy loads number field with priority
    // Legacy saves: val != '' ? '"val"' : 'null' (line 7793)
    // Legacy loads: number != undefined ? number : '0' (line 4270)
    // So if number is null in legacy, it was saved as empty (user cleared field)
    // If number is undefined, legacy defaults to '0' but we treat as not set
    // We should only set phoneNumber if number exists and is not null/empty
    const numberValue = getFieldValueFromHtml(html, ['number'])
    if (numberValue !== undefined && numberValue !== null) {
      const numberStr = String(numberValue).trim()
      // Legacy uses '0' as default placeholder when undefined, but if explicitly saved as '0', treat as valid
      // However, empty string should be treated as not set
      if (numberStr !== '' && numberStr !== '0') {
        externalTransferNodeData.phoneNumber = numberStr
      } else if (numberStr === '0') {
        // '0' is legacy's default when undefined - don't set phoneNumber to allow other checks
        // This matches legacy behavior where '0' means "not set, use phone_number_id if available"
      }
    } else {
      // number is undefined/null - check for phone_number or phoneNumber as fallback
      const phoneNumber = getStringFieldValue(html, [
        'phone_number',
        'phoneNumber',
      ])
      if (phoneNumber) {
        externalTransferNodeData.phoneNumber = phoneNumber
      }
    }

    if (
      !externalTransferNodeData.phoneNumberId &&
      !externalTransferNodeData.phoneNumber
    ) {
      if ('caller_id' in html && html.caller_id) {
        externalTransferNodeData.callerId = String(html.caller_id)
      } else if ('callerId' in html && html.callerId) {
        externalTransferNodeData.callerId = String(html.callerId)
      }
    }

    if ('press_1' in html && html.press_1 !== undefined) {
      const press1Value = String(html.press_1)
      externalTransferNodeData.requireOneToConnect = press1Value === '1'
    } else if (
      'require_one_to_connect' in html &&
      html.require_one_to_connect !== undefined
    ) {
      externalTransferNodeData.requireOneToConnect = Boolean(
        html.require_one_to_connect,
      )
    } else if (
      'requireOneToConnect' in html &&
      html.requireOneToConnect !== undefined
    ) {
      externalTransferNodeData.requireOneToConnect = Boolean(
        html.requireOneToConnect,
      )
    } else {
      // Default to true to match legacy behavior (new nodes have checkbox checked)
      // Note: Legacy always saves press_1, so this default is rarely (if ever) hit
      externalTransferNodeData.requireOneToConnect = true
    }

    if ('minimized' in html) {
      externalTransferNodeData.minimized = stringToBoolean(html.minimized)
    }
  }
}

/**
 * Map auto attendant node fields from legacy HTML to React Flow node data
 */
export function mapAutoAttendantFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'auto_attendant', NodeType.AutoAttendant)) {
    const autoAttendantNodeData = nodeData as AutoAttendantNodeData &
      NodeDataRecord

    mapFieldWithNullConversion(
      html,
      autoAttendantNodeData,
      'sound_clip_id',
      'soundClipId',
    )
    mapFieldWithNullConversion(
      html,
      autoAttendantNodeData,
      'sound_clip_uuid',
      'soundClipUuid',
    )
    if (
      'greeting' in html &&
      html.greeting &&
      !autoAttendantNodeData.soundClipId
    ) {
      autoAttendantNodeData.soundClipId = String(html.greeting)
    }

    mapTimeout(html, autoAttendantNodeData)

    if (
      'repeat_times' in html &&
      html.repeat_times !== undefined &&
      html.repeat_times !== null &&
      String(html.repeat_times).trim() !== ''
    ) {
      const repeatValue = parseInt(String(html.repeat_times), 10)
      if (!isNaN(repeatValue)) {
        autoAttendantNodeData.repeatTimes = repeatValue
      }
    } else if (
      'repeatTimes' in html &&
      html.repeatTimes !== undefined &&
      html.repeatTimes !== null &&
      String(html.repeatTimes).trim() !== ''
    ) {
      const repeatValue = parseInt(String(html.repeatTimes), 10)
      if (!isNaN(repeatValue)) {
        autoAttendantNodeData.repeatTimes = repeatValue
      }
    }

    if (
      'allow_extension_dialing' in html &&
      html.allow_extension_dialing !== undefined
    ) {
      autoAttendantNodeData.allowExtensionDialing = stringToBoolean(
        html.allow_extension_dialing,
      )
    } else if (
      'allowExtensionDialing' in html &&
      html.allowExtensionDialing !== undefined
    ) {
      autoAttendantNodeData.allowExtensionDialing = stringToBoolean(
        html.allowExtensionDialing,
      )
    }

    const normalizeOption = (opt: unknown): AutoAttendantOption | null => {
      if (!opt || typeof opt !== 'object') {
        if (opt !== null && opt !== undefined) {
          return {
            digit: String(opt),
            action: 'transfer',
          }
        }
        return null
      }

      const option = opt as Record<string, unknown>

      const digit =
        option.digit ||
        option.digit_value ||
        option.press_id ||
        option.press ||
        option.value ||
        option.option ||
        option.option_id ||
        option.key ||
        (option.id && !String(option.id).includes('_option-')
          ? option.id
          : null)

      // PRESERVE option ID if it exists and contains '_option-'
      // This ensures round-trip compatibility - option IDs are critical for routing
      const optionId =
        option.id && String(option.id).includes('_option-')
          ? String(option.id)
          : undefined

      const action =
        (option.action as AutoAttendantOption['action']) ||
        (option.action_type as AutoAttendantOption['action']) ||
        'transfer'

      const target = extractTarget(option)

      if (digit) {
        const normalized: AutoAttendantOption = {
          digit: String(digit),
          action: action as AutoAttendantOption['action'],
        }
        if (target) {
          normalized.target = String(target)
        }
        // PRESERVE option ID if it exists and contains '_option-'
        if (optionId) {
          normalized.id = optionId
        }
        return normalized
      }

      if (Object.keys(option).length === 0) {
        return null
      }

      const nonIdFields = Object.keys(option).filter(
        (key) =>
          !key.includes('id') &&
          !key.includes('uuid') &&
          key !== 'target' &&
          key !== 'action',
      )
      if (nonIdFields.length > 0) {
        const inferredDigit = option[nonIdFields[0]]
        if (inferredDigit) {
          return {
            digit: String(inferredDigit),
            action: action as AutoAttendantOption['action'],
            target: target ? String(target) : undefined,
          }
        }
      }

      return null
    }

    if ('options' in html && Array.isArray(html.options)) {
      const mappedOptions = html.options
        .map(normalizeOption)
        .filter((opt): opt is AutoAttendantOption => opt !== null)
      if (mappedOptions.length > 0) {
        autoAttendantNodeData.options = mappedOptions
        mappedOptions.forEach((opt, index) => {
          if (opt.target) {
            autoAttendantNodeData[`option-${index}`] = opt.target
          }
        })
      }
    } else if ('children' in html && Array.isArray(html.children)) {
      const childrenArray = normalizeChildrenArray(html.children)

      const mappedOptions = childrenArray
        .slice(1)
        .map(normalizeOption)
        .filter((opt): opt is AutoAttendantOption => opt !== null)
      if (mappedOptions.length > 0) {
        autoAttendantNodeData.options = mappedOptions
        mappedOptions.forEach((opt, index) => {
          if (opt.target) {
            autoAttendantNodeData[`option-${index}`] = opt.target
          }
        })
      }
    } else {
      const optionFields: AutoAttendantOption[] = []
      Object.keys(html).forEach((key) => {
        if (key.startsWith('option-') || key.match(/^option\d+$/)) {
          const digit = key.replace(/^option-?/, '').replace(/^option/, '')
          const target = html[key]
          if (digit) {
            optionFields.push({
              digit,
              action: 'transfer',
              target: target ? String(target) : undefined,
            })
          }
        }
      })
      if (optionFields.length > 0) {
        autoAttendantNodeData.options = optionFields
      }
    }

    mapOnInvalidAndOnTimeout(html, autoAttendantNodeData)
  }
}

/**
 * Map SMS notification node fields from legacy HTML to React Flow node data
 */
export function mapSmsNotificationFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  isSmsNotification: boolean,
  html: HtmlFieldRecord,
): void {
  if (
    kind === 'sms_notification' ||
    nodeType === NodeType.SmsNotification ||
    isSmsNotification
  ) {
    if ('option' in html) {
      const optionValue = String(html.option)
      if (optionValue === '1') {
        nodeData.messageType = 'Missed call'
      } else if (optionValue === '2') {
        nodeData.messageType = 'Incoming call'
      } else if (optionValue === '3') {
        nodeData.messageType = 'Voicemail'
      } else if (optionValue === '4') {
        nodeData.messageType = 'Custom'
      }
    }

    if (!nodeData.messageType && 'message' in html) {
      const message = String(html.message)
      if (message.includes('went to Voicemail')) {
        nodeData.messageType = 'Voicemail'
      } else if (message.includes('Incoming call from')) {
        nodeData.messageType = 'Incoming call'
      } else if (message.includes('Missed call from')) {
        nodeData.messageType = 'Missed call'
      }
    }

    if ('sms_recipient' in html && html.sms_recipient) {
      nodeData.phoneNumber = String(html.sms_recipient)
    } else if ('phone_number' in html) {
      nodeData.phoneNumber = String(html.phone_number)
    } else if ('phoneNumber' in html) {
      nodeData.phoneNumber = String(html.phoneNumber)
    }

    if ('auto_reply' in html) {
      nodeData.autoReplyToCaller = stringToBoolean(html.auto_reply)
    }

    if ('message' in html) {
      nodeData.message = String(html.message)
    }

    if ('sms_number' in html && html.sms_number) {
      nodeData.sendFrom = String(html.sms_number)
    }

    if ('minimized' in html) {
      nodeData.minimized = stringToBoolean(html.minimized)
    }
  }
}

/**
 * Map fax detect node fields from legacy HTML to React Flow node data
 */
export function mapFaxDetectFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'fax_detect', NodeType.FaxDetect)) {
    const faxNodeData = nodeData as FaxDetectNodeData & NodeDataRecord

    const faxExtensionId = getStringFieldValue(html, [
      'fax_extension_id',
      'faxExtensionId',
      'fax_destination',
      'faxDestination',
    ])
    if (faxExtensionId) {
      faxNodeData.faxExtensionId = faxExtensionId
      // If it came from fax_destination, also set that field
      if (
        ('fax_destination' in html && html.fax_destination) ||
        ('faxDestination' in html && html.faxDestination)
      ) {
        faxNodeData.faxDestination = faxExtensionId
      }
    }

    const voicePath = getStringFieldValue(html, ['voice_path', 'voicePath'])
    if (voicePath) {
      faxNodeData.voicePath = voicePath
    }
  }
}

/**
 * Map interactive webhook node fields from legacy HTML to React Flow node data
 */
export function mapInteractiveWebhookFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (
    kind === 'interactive_webhook' ||
    nodeType === NodeType.InteractiveWebhook
  ) {
    const webhookNodeData = nodeData as InteractiveWebhookNodeData &
      NodeDataRecord

    mapFieldWithNullConversion(html, webhookNodeData, 'iwebhook_url', 'url')
    if (!webhookNodeData.url) {
      if ('url' in html && html.url) {
        webhookNodeData.url = String(html.url)
      } else if ('iwebhookUrl' in html && html.iwebhookUrl) {
        webhookNodeData.url = String(html.iwebhookUrl)
      }
    }

    if (
      'iwebhook_allow_extension_dialing' in html ||
      'allowExtensionAsOption' in html ||
      'allow_extension_as_option' in html
    ) {
      const allowExtension =
        html.iwebhook_allow_extension_dialing ||
        html.allowExtensionAsOption ||
        html.allow_extension_as_option
      webhookNodeData.allowExtensionAsOption = Boolean(allowExtension)
    }

    if ('children' in html && html.children) {
      const children = html.children
      if (Array.isArray(children)) {
        const childrenArray = normalizeChildrenArray(children)

        const mappedOptions = childrenArray
          .slice(1)
          .map((child: unknown, filteredIndex: number) => {
            if (typeof child === 'object' && child !== null) {
              const childObj = child as Record<string, unknown>
              const value =
                childObj.value ||
                childObj.option ||
                childObj.option_id ||
                (childObj.id && !String(childObj.id).includes('_option-')
                  ? childObj.id
                  : null)

              // PRESERVE option ID if it exists and contains '_option-'
              // This ensures round-trip compatibility - option IDs are critical for routing
              const optionId =
                childObj.id && String(childObj.id).includes('_option-')
                  ? String(childObj.id)
                  : undefined
              const label = childObj.label
              const target = extractTarget(childObj)
              if (Object.keys(childObj).length === 0) {
                return null
              }
              if (value || label) {
                if (target) {
                  webhookNodeData[`option-${filteredIndex}`] = String(target)
                }
                const normalized: InteractiveWebhookOption = {
                  value: value ? String(value) : '',
                  label: label ? String(label) : undefined,
                }
                // PRESERVE option ID if it exists and contains '_option-'
                if (optionId) {
                  normalized.id = optionId
                }
                return normalized
              }
              return null
            }
            if (child) {
              return {
                value: String(child),
              } as InteractiveWebhookOption
            }
            return null
          })
          .filter((opt): opt is InteractiveWebhookOption => opt !== null)
        if (mappedOptions.length > 0) {
          webhookNodeData.options = mappedOptions
        }
      }
    } else if ('options' in html && html.options) {
      const options = html.options
      if (Array.isArray(options)) {
        const mappedOptions = options
          .map((opt: unknown, index: number) => {
            if (typeof opt === 'object' && opt !== null) {
              const optObj = opt as Record<string, unknown>
              const value =
                optObj.value ||
                optObj.option ||
                optObj.option_id ||
                (optObj.id && !String(optObj.id).includes('_option-')
                  ? optObj.id
                  : null)
              const label = optObj.label
              const target = extractTarget(optObj)
              if (Object.keys(optObj).length === 0) {
                return null
              }
              if (value || label) {
                if (target) {
                  webhookNodeData[`option-${index}`] = String(target)
                }
                return {
                  value: value ? String(value) : undefined,
                  label: label ? String(label) : undefined,
                } as InteractiveWebhookOption
              }
              return null
            }
            if (opt) {
              return {
                value: String(opt),
              } as InteractiveWebhookOption
            }
            return null
          })
          .filter((opt): opt is InteractiveWebhookOption => opt !== null)
        if (mappedOptions.length > 0) {
          webhookNodeData.options = mappedOptions
        }
      }
    }

    Object.keys(html).forEach((key) => {
      if (key.startsWith('option-') && html[key]) {
        webhookNodeData[key] = String(html[key])
      }
    })

    mapOnInvalidAndOnTimeout(html, webhookNodeData)
  }
}

/**
 * Map webhook node fields from legacy HTML to React Flow node data
 */
export function mapWebhookFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (kind === 'webhook' || nodeType === NodeType.Webhook) {
    const webhookNodeData = nodeData as NodeDataRecord

    mapFieldWithNullConversion(html, webhookNodeData, 'webhook_id', 'webhookId')

    mapFieldWithNullConversion(html, webhookNodeData, 'notify_url', 'url')
    if (!webhookNodeData.url && 'url' in html && html.url) {
      webhookNodeData.url = String(html.url)
    }
  }
}

/**
 * Map change caller ID node fields from legacy HTML to React Flow node data
 */
export function mapChangeCallerIdFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'change_caller_id', NodeType.ChangeCallerId)) {
    const callerIdNodeData = nodeData as ChangeCallerIdNodeData & NodeDataRecord

    const newCallerId = getStringFieldValue(html, [
      'new_caller_id',
      'newCallerId',
      'caller_id_text',
    ])
    if (newCallerId) {
      callerIdNodeData.newCallerId = newCallerId
    }

    if ('mode' in html && html.mode) {
      const mode = String(html.mode)
      if (mode === 'replace' || mode === 'add_front') {
        callerIdNodeData.mode = mode
      }
    } else if ('caller_id_mode' in html && html.caller_id_mode) {
      const mode = String(html.caller_id_mode)
      if (mode === 'replace' || mode === 'add_front') {
        callerIdNodeData.mode = mode
      }
    } else if ('replace_caller_id' in html && html.replace_caller_id) {
      callerIdNodeData.mode = html.replace_caller_id ? 'replace' : 'add_front'
    }

    // Map new_caller_id_option to mode if mode is not already set
    // This handles legacy data that only has new_caller_id_option
    if (!callerIdNodeData.mode) {
      if ('new_caller_id_option' in html && html.new_caller_id_option) {
        const option = String(html.new_caller_id_option)
        if (option === 'replace_caller') {
          callerIdNodeData.mode = 'replace'
        } else if (
          option === 'six_char' ||
          option === 'undefined' ||
          option === 'null'
        ) {
          // "six_char" maps to "add_front", and "undefined"/"null" should default to "add_front"
          callerIdNodeData.mode = 'add_front'
        }
      } else if ('newCallerIdOption' in html && html.newCallerIdOption) {
        const option = String(html.newCallerIdOption)
        if (option === 'replace_caller') {
          callerIdNodeData.mode = 'replace'
        } else if (
          option === 'six_char' ||
          option === 'undefined' ||
          option === 'null'
        ) {
          callerIdNodeData.mode = 'add_front'
        }
      } else {
        // Default to 'add_front' if no mode or new_caller_id_option is set
        callerIdNodeData.mode = 'add_front'
      }
    }

    if ('minimized' in html) {
      callerIdNodeData.minimized = stringToBoolean(html.minimized)
    }

    if ('new_caller_id_option' in html && html.new_caller_id_option) {
      const option = String(html.new_caller_id_option)
      if (option === 'undefined' || option === 'null') {
        // Handle legacy "undefined" string case - mode already set above
        callerIdNodeData.newCallerIdOption = null
      } else {
        callerIdNodeData.newCallerIdOption = option
      }
    } else if ('newCallerIdOption' in html && html.newCallerIdOption) {
      const option = String(html.newCallerIdOption)
      if (option === 'undefined' || option === 'null') {
        // Handle legacy "undefined" string case - mode already set above
        callerIdNodeData.newCallerIdOption = null
      } else {
        callerIdNodeData.newCallerIdOption = option
      }
    }
  }
}

/**
 * Map wait node fields from legacy HTML to React Flow node data
 */
export function mapWaitFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'wait', NodeType.Wait)) {
    const waitNodeData = nodeData as WaitNodeData & NodeDataRecord

    if ('duration' in html && html.duration !== undefined) {
      const durationValue = html.duration
      waitNodeData.duration =
        typeof durationValue === 'number'
          ? durationValue
          : parseInt(String(durationValue), 10)
      if (isNaN(waitNodeData.duration as number)) {
        delete waitNodeData.duration
      }
    } else if ('wait_duration' in html && html.wait_duration !== undefined) {
      const durationValue = html.wait_duration
      waitNodeData.duration =
        typeof durationValue === 'number'
          ? durationValue
          : parseInt(String(durationValue), 10)
      if (isNaN(waitNodeData.duration as number)) {
        delete waitNodeData.duration
      }
    }

    if ('ring_during_wait' in html) {
      waitNodeData.ringDuringWait = stringToBoolean(html.ring_during_wait)
    } else if ('ringDuringWait' in html) {
      waitNodeData.ringDuringWait = stringToBoolean(html.ringDuringWait)
    } else if ('ring_while_waiting' in html) {
      waitNodeData.ringDuringWait = stringToBoolean(html.ring_while_waiting)
    }
  }
}

/**
 * Map conference room node fields from legacy HTML to React Flow node data
 */
export function mapConferenceRoomFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'conference_room', NodeType.ConferenceRoom)) {
    const conferenceRoomNodeData = nodeData as ConferenceRoomNodeData &
      NodeDataRecord

    const conferenceRoomId = getStringFieldValue(html, [
      'conference_room_id',
      'conferenceRoomId',
      'extension_id',
      'extensionId',
    ])
    if (conferenceRoomId) {
      conferenceRoomNodeData.conferenceRoomId = conferenceRoomId
    }
  }
}

/**
 * Map jump to switchboard node fields from legacy HTML to React Flow node data
 */
export function mapJumpToSwitchboardFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (
    kind === 'jump_to_switchboard' ||
    kind === 'jump_to_dial_plan' ||
    nodeType === NodeType.JumpToSwitchboard
  ) {
    const jumpToSwitchboardNodeData = nodeData as JumpToSwitchboardNodeData &
      NodeDataRecord

    const targetSwitchboardId = getStringFieldValue(html, [
      'target_switchboard_id',
      'targetSwitchboardId',
      'switchboard_id',
      'switchboardId',
      'extension_id',
      'extensionId',
      'dial_plan_id',
    ])
    if (targetSwitchboardId) {
      jumpToSwitchboardNodeData.targetSwitchboardId = targetSwitchboardId
    }
  }
}

/**
 * Map call distribution node fields from legacy HTML to React Flow node data
 */
export function mapCallDistributionFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (
    isNodeType(kind, nodeType, 'call_distribution', NodeType.CallDistribution)
  ) {
    const callDistributionNodeData = nodeData as CallDistributionNodeData &
      NodeDataRecord

    mapTimeout(html, callDistributionNodeData)

    if ('options' in html && Array.isArray(html.options)) {
      callDistributionNodeData.options = html.options.map((opt: unknown) => {
        const option = opt as Record<string, unknown>
        return {
          weight:
            option.weight !== undefined
              ? parseInt(String(option.weight), 10)
              : undefined,
        }
      })
      if ('children' in callDistributionNodeData) {
        delete callDistributionNodeData.children
      }
    } else if ('weights' in html && Array.isArray(html.weights)) {
      callDistributionNodeData.options = html.weights.map(
        (weight: unknown) => ({
          weight:
            weight !== undefined ? parseInt(String(weight), 10) : undefined,
        }),
      )
      if ('children' in callDistributionNodeData) {
        delete callDistributionNodeData.children
      }
    } else if ('children' in html && Array.isArray(html.children)) {
      // Auto-fix: Ensure children array starts with empty object {}
      // Legacy format requires first element to be empty object, but handle malformed data gracefully
      let childrenArray = html.children
      if (childrenArray.length > 0) {
        const firstElement = childrenArray[0]
        // If first element is not an empty object, prepend one
        if (
          typeof firstElement !== 'object' ||
          firstElement === null ||
          Object.keys(firstElement as Record<string, unknown>).length > 0
        ) {
          childrenArray = [{}, ...childrenArray]
        }
      } else {
        // If array is empty, add empty object as first element
        childrenArray = [{}]
      }

      const mappedOptions = childrenArray
        .filter((child, index) => {
          if (index === 0) {
            return false
          }
          if (child === null || child === undefined) {
            return false
          }
          if (
            typeof child === 'object' &&
            Object.keys(child as Record<string, unknown>).length === 0
          ) {
            return false
          }
          return true
        })
        .map((child: unknown, _filteredIndex: number) => {
          let targetId: string | undefined
          let optionId: string | undefined

          if (typeof child === 'object' && child !== null) {
            const childObj = child as Record<string, unknown>
            const extractedTarget = extractTarget(childObj)
            targetId = extractedTarget ? String(extractedTarget) : undefined

            // PRESERVE option ID if it exists and contains '_option-'
            // This ensures round-trip compatibility - option IDs are critical for routing
            if (childObj.id && String(childObj.id).includes('_option-')) {
              optionId = String(childObj.id)
            }
          }

          if (typeof child === 'number') {
            return {
              weight: child,
              targetId,
              ...(optionId ? { id: optionId } : {}),
            }
          } else if (typeof child === 'string') {
            const weight = parseInt(child, 10)
            return {
              weight: isNaN(weight) ? undefined : weight,
              targetId,
              ...(optionId ? { id: optionId } : {}),
            }
          } else if (typeof child === 'object' && child !== null) {
            const childObj = child as Record<string, unknown>
            const weightValue =
              childObj.weight !== undefined
                ? childObj.weight
                : childObj.value !== undefined
                  ? childObj.value
                  : childObj.weight_id !== undefined
                    ? childObj.weight_id
                    : undefined
            return {
              weight:
                weightValue !== undefined
                  ? parseInt(String(weightValue), 10)
                  : undefined,
              targetId,
              ...(optionId ? { id: optionId } : {}),
            }
          }
          return {
            weight: undefined,
            targetId,
            ...(optionId ? { id: optionId } : {}),
          }
        })
        .filter((opt) => {
          return opt.weight !== undefined && !isNaN(opt.weight as number)
        })

      callDistributionNodeData.options = mappedOptions.map(
        ({ targetId: _targetId, ...opt }) => opt as CallDistributionOption,
      )
      mappedOptions.forEach((opt, index) => {
        if (opt.targetId) {
          callDistributionNodeData[`option-${index}`] = opt.targetId
        }
      })
      delete callDistributionNodeData.children
    } else {
      callDistributionNodeData.options = []
      if ('children' in callDistributionNodeData) {
        delete callDistributionNodeData.children
      }
    }
  }
}

/**
 * Map sound clip node fields from legacy HTML to React Flow node data
 */
export function mapSoundClipFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'sound_clip', NodeType.SoundClip)) {
    const soundClipNodeData = nodeData as SoundClipNodeData & NodeDataRecord

    mapFieldWithNullConversion(
      html,
      soundClipNodeData,
      'sound_clip_id',
      'soundClipId',
    )
    mapFieldWithNullConversion(
      html,
      soundClipNodeData,
      'sound_clip_uuid',
      'soundClipUuid',
    )
  }
}

/**
 * Map dial by name node fields from legacy HTML to React Flow node data
 */
export function mapDialByNameFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'dial_name', NodeType.DialByName)) {
    const dialByNameNodeData = nodeData as DialByNameNodeData & NodeDataRecord

    if ('lookup_id' in html && html.lookup_id) {
      const lookupId = String(html.lookup_id)
      if (lookupId === 'undefined' || lookupId === 'null') {
        dialByNameNodeData.lookupId = undefined
      } else if (lookupId === 'extension_name' || lookupId === 'username') {
        dialByNameNodeData.lookupId = lookupId as 'extension_name' | 'username'
      } else {
        dialByNameNodeData.lookupId = lookupId as 'extension_name' | 'username'
      }
    } else if ('lookupId' in html && html.lookupId) {
      const lookupId = String(html.lookupId)
      if (lookupId === 'extension_name' || lookupId === 'username') {
        dialByNameNodeData.lookupId = lookupId as 'extension_name' | 'username'
      }
    } else {
      dialByNameNodeData.lookupId = 'extension_name'
    }

    if ('sort_order' in html && html.sort_order) {
      const sortOrder = String(html.sort_order)
      if (
        sortOrder === 'first_name' ||
        sortOrder === 'last_name' ||
        sortOrder === 'first_last_name'
      ) {
        dialByNameNodeData.sortOrder = sortOrder as
          'first_name' | 'last_name' | 'first_last_name'
      }
    } else if ('sortOrder' in html && html.sortOrder) {
      const sortOrder = String(html.sortOrder)
      if (
        sortOrder === 'first_name' ||
        sortOrder === 'last_name' ||
        sortOrder === 'first_last_name'
      ) {
        dialByNameNodeData.sortOrder = sortOrder as
          'first_name' | 'last_name' | 'first_last_name'
      }
    } else {
      dialByNameNodeData.sortOrder = 'first_name'
    }

    if ('directory_extension_id' in html && html.directory_extension_id) {
      dialByNameNodeData.directoryExtensionId = String(
        html.directory_extension_id,
      )
    } else if ('directoryExtensionId' in html && html.directoryExtensionId) {
      dialByNameNodeData.directoryExtensionId = String(
        html.directoryExtensionId,
      )
    }
  }
}

/**
 * Map AI agent v1 node fields from legacy HTML to React Flow node data
 */
export function mapAiAgentV1Fields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'ai_agent_v1', NodeType.AiAgentV1)) {
    if ('agent_id' in html) {
      const agentId = html.agent_id
      nodeData.agentId =
        agentId !== null && agentId !== undefined ? String(agentId) : null
    } else if ('agentId' in html) {
      const agentId = html.agentId
      nodeData.agentId =
        agentId !== null && agentId !== undefined ? String(agentId) : null
    }
  }
}

/**
 * Map day/night node fields from legacy HTML to React Flow node data
 */
export function mapDayNightFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'day_night', NodeType.DayNight)) {
    const dayNightNodeData = nodeData as DayNightNodeData & NodeDataRecord

    if ('choice_id' in html && html.choice_id) {
      const code = String(html.choice_id)
      dayNightNodeData.toggleCode = `*${code}`
    } else if ('toggle_code' in html && html.toggle_code) {
      dayNightNodeData.toggleCode = String(html.toggle_code)
    } else if ('toggleCode' in html && html.toggleCode) {
      dayNightNodeData.toggleCode = String(html.toggleCode)
    } else {
      dayNightNodeData.toggleCode = null
    }

    if ('status' in html && html.status) {
      const status = String(html.status).toLowerCase()
      if (status === 'open' || status === 'closed') {
        dayNightNodeData.status = status as 'open' | 'closed'
      }
    }

    if ('day_path' in html && html.day_path) {
      dayNightNodeData.dayPath = String(html.day_path)
      dayNightNodeData.whenOpen = String(html.day_path)
    } else if ('dayPath' in html && html.dayPath) {
      dayNightNodeData.dayPath = String(html.dayPath)
      dayNightNodeData.whenOpen = String(html.dayPath)
    }

    if ('night_path' in html && html.night_path) {
      dayNightNodeData.nightPath = String(html.night_path)
      dayNightNodeData.whenClosed = String(html.night_path)
    } else if ('nightPath' in html && html.nightPath) {
      dayNightNodeData.nightPath = String(html.nightPath)
      dayNightNodeData.whenClosed = String(html.nightPath)
    }

    mapField(html, dayNightNodeData, 'when_open', 'whenOpen')
    mapField(html, dayNightNodeData, 'when_closed', 'whenClosed')
  }
}

/**
 * Map queue node fields from legacy HTML to React Flow node data
 */
/**
 * Map queue node fields from legacy HTML to React Flow node data
 * Legacy format stores queue_id and max_wait_time in HTML.
 * Connection targets (onTimeout, onEscape) can be in HTML fields
 * or in endpoints.sources[].target_uuid (extracted from edges).
 */
export function mapQueueFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (isNodeType(kind, nodeType, 'queue', NodeType.Queue)) {
    const queueNodeData = nodeData as QueueNodeData & NodeDataRecord

    // Map queue_id
    mapField(html, queueNodeData, 'queue_id', 'queueId')
    // Map max_wait_time to maxWaitTime
    if ('max_wait_time' in html && html.max_wait_time !== undefined) {
      const maxWaitTime = html.max_wait_time
      queueNodeData.maxWaitTime =
        typeof maxWaitTime === 'number'
          ? maxWaitTime
          : parseInt(String(maxWaitTime), 10)
      if (isNaN(queueNodeData.maxWaitTime as number)) {
        delete queueNodeData.maxWaitTime
      }
    }
    // Map connection fields from HTML (if present)
    mapField(html, queueNodeData, 'on_timeout', 'onTimeout')
    mapField(html, queueNodeData, 'on_escape', 'onEscape')
  } else if ('queue_id' in html) {
    nodeData.queueId = String(html.queue_id)
  }
}

/**
 * Map transfer rollover node fields from legacy HTML to React Flow node data
 */
export function mapTransferRolloverFields(
  legacyNode: LegacyFormatNode,
  nodeData: NodeDataRecord,
  kind: string,
  nodeType: NodeType | 'start',
  html: HtmlFieldRecord,
): void {
  if (
    kind === 'transfer_rollover' ||
    nodeType === NodeType.TransferAndRollover
  ) {
    const nodeDataRecord = nodeData as NodeDataRecord

    if (
      'extensions' in nodeData &&
      Array.isArray(nodeData.extensions) &&
      nodeData.extensions.length > 0
    ) {
      nodeData.extensions = (nodeData.extensions as unknown[]).map(
        (ext: unknown) => String(ext),
      )
    } else {
      const htmlRecord = (html || {}) as Record<string, unknown>
      const extensionId =
        htmlRecord.transfer_rollover_id ||
        nodeDataRecord.transfer_rollover_id ||
        htmlRecord.extension_id ||
        nodeDataRecord.extension_id ||
        nodeData.extensionId

      if (
        extensionId !== undefined &&
        extensionId !== null &&
        extensionId !== ''
      ) {
        const extIdString = String(extensionId)
        nodeData.extensionId = extIdString === 'null' ? null : extIdString
        nodeData.extensions = [extIdString]
      }
    }

    mapField(html, nodeData, 'no_answer', 'noAnswer')
    if ('busy' in html && html.busy) {
      nodeData.busy = String(html.busy)
    }

    delete nodeDataRecord.extension_id
    delete nodeDataRecord.transfer_rollover_id
  }
}
